import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  UseQueryResult
} from "@tanstack/react-query";
import { AxiosPromise, AxiosResponse } from "axios";
import { snakeCase } from "lodash";
import { useCallback, useEffect, useRef } from "react";
import { useUnmount } from "react-use";
import useUploadListener from "../../../hooks/upload/useUploadListener";
import { ErrorResponse, MessageErrorResponse } from "../../../requests/@types";
import { LocalSearchResponse } from "../../../services/@types";
import { advancedSearchKeys, FETCH_LOCAL_DATA } from "../../samples/queryKeys";

import { SearchProps } from "../@types";
import api from "../api";
import { AVAILABLE_TRUE_QP, LOCATION } from "../consts";
import {
  constructQuery,
  constructSelections,
  createFilterChangeSelections,
  determineSort
} from "../util";
import { DEFAULT_PAGE } from "./consts";

/**
 * Hook used to retrieve local search data
 *
 * @param props {args, options}
 * args - arguments for api call
 * options - options for useQuery hook
 */
export const useLocalSearch = (props: SearchProps) => {
  const {
    args: { params },
    options
  } = props;
  const abortControllerRef = useRef<AbortController | null>(null);

  useUnmount(() => {
    abortControllerRef.current?.abort();
  });

  const { q, selections, page, pagesize, sort, location, isRecent } =
    params || {};
  const isLocal = location === LOCATION.LOCAL;
  const reqPage = isLocal ? page : DEFAULT_PAGE;
  const reqPageSize = isLocal ? pagesize : 0;

  const queryClient = useQueryClient();

  useUploadListener(() =>
    queryClient.invalidateQueries({ queryKey: [FETCH_LOCAL_DATA] })
  );

  const localSelections = createFilterChangeSelections(
    LOCATION.LOCAL,
    selections
  );

  const queryKey = advancedSearchKeys.fetchLocalDataKey({
    q,
    selections: localSelections,
    page: reqPage,
    pagesize: reqPageSize,
    sort
  });

  const search = () => {
    abortControllerRef.current?.abort("Aborting previous call.");
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    return api.retrieveAdvancedSearchSamples(
      {
        q: q ? constructQuery(q, AVAILABLE_TRUE_QP) : "",
        location: LOCATION.LOCAL,
        page: reqPage,
        pagesize: reqPageSize,
        sort: determineSort(LOCATION.LOCAL, sort),
        ...(isLocal && isRecent ? { recent: true } : {}),
        ...constructSelections(localSelections, snakeCase)
      },
      abortController.signal
    ) as AxiosPromise<LocalSearchResponse>;
  };

  const localQuery = useQuery({
    queryKey,
    queryFn: search,
    placeholderData: keepPreviousData,
    refetchInterval: options.refetchInterval,
    enabled: options.enabled,
    select: (response: AxiosResponse<LocalSearchResponse>) => {
      if (options.select) {
        return options.select(response);
      }
      return response.data;
    }
  });

  const { status, fetchStatus, data, error } = localQuery;

  useEffect(() => {
    if (status === "success" && fetchStatus === "idle" && options.onSuccess) {
      options.onSuccess(data as LocalSearchResponse);
    } else if (status === "error" && fetchStatus === "idle" && options.onError)
      options.onError(error as unknown as ErrorResponse<MessageErrorResponse>);
  }, [data, status, fetchStatus, error]);

  const abortLocal = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  return {
    localQuery: localQuery as UseQueryResult<
      LocalSearchResponse,
      ErrorResponse<MessageErrorResponse>
    >,
    abortLocal
  };
};
